import { useAsyncStorage } from '@react-native-async-storage/async-storage'
import { authUpgrade, logout, restoreSession } from '@services/api/auth'
import { client } from '@services/api/client'
import { AUTH_TOKEN } from '@constants/storages'
import {
  useState,
  ReactNode,
  createContext,
  useEffect,
  useCallback,
  useMemo,
} from 'react'
import { isWeb } from 'tamagui'
import { router, usePathname, useRootNavigationState } from 'expo-router'
import { isGuestRoute, isOptionalAuthRoute, isPublic } from '@utils/routes'
import { useRudderstack } from '@hooks/useAnalytics'
import { jwtDecode } from 'jwt-decode'
import { apolloClient } from '@services/api/apollo'
import { LoadingScreen } from '@components/LoadingScreen'
import { useGetUser } from '@hooks/useGetUser'
import { IUser } from '@dtos/User'
import { useHandleWebHomeToRedirect } from '@hooks/handleWebHomeToRedirect'

interface ISessionProviderProps {
  logged: boolean
  loggedLoading: boolean
  token: string | null
  user: IUser | null
  signInByToken: (token: string | null) => Promise<boolean>
}

interface IJWTHeader {
  sub: number
}

export const SessionContext = createContext<ISessionProviderProps>(
  {} as ISessionProviderProps,
)

let hasIdentified = false
let isLoggingOut = false

export function SessionProvider({ children }: { children: ReactNode }) {
  const rootNavigationState = useRootNavigationState()
  const [logged, setLogged] = useState(false)
  const [loggedLoading, setLoggedLoading] = useState(true)
  const [user, setUser] = useState<IUser | null>(null)
  const [token, setSessionToken] = useState<string | null>(null)
  const { getUser } = useGetUser()
  const pathname = usePathname()
  const {
    getItem: getToken,
    setItem: setToken,
    removeItem: removeToken,
  } = useAsyncStorage(AUTH_TOKEN)
  const { identify } = useRudderstack()
  const handleWebHomeToRedirect = useHandleWebHomeToRedirect()

  const shouldSkipRoute = isGuestRoute(pathname) || isPublic

  const handleLogout = useCallback(
    async ({ revokeSession = true } = {}) => {
      // Efeitos e erros concorrentes chamam handleLogout em paralelo; cada
      // DELETE /users/sign_out extra derruba a sessão recém-criada no core
      if (isLoggingOut) return
      isLoggingOut = true
      await removeToken()
      setSessionToken(null)
      const redirect = () => {
        if (isWeb) {
          window.location.href = handleWebHomeToRedirect
        } else router.replace('/users/sign_in')
      }
      // Sem sessão válida no core (upgrade falhou) não há o que revogar:
      // o sign_out aqui derrubaria a sessão que outro device/aba criou
      if (!revokeSession) {
        redirect()
        return
      }
      await logout().finally(redirect)
    },
    [handleWebHomeToRedirect, removeToken, setSessionToken],
  )

  // true = autenticou (mesma semântica do AppSessionContext, que as telas de
  // login/convite/reset consomem).
  const signInByToken = useCallback(
    async (token: string | null) => {
      if (!token) return false
      try {
        client.defaults.headers.Authorization = `Bearer ${token}`
        apolloClient.defaultContext.headers = {
          authorization: `Bearer ${token}`,
        }
        const { data, error } = await getUser()
        if (data) {
          setUser(data.user)
        }
        if (error) {
          return false
        }
        setLoggedLoading(false)
        setLogged(true)
        setSessionToken(token)
        return true
      } catch {
        return false
      }
    },
    [getUser],
  )

  const identifyOnce = useCallback(
    (token: string) => {
      try {
        if (hasIdentified) return
        const { sub } = jwtDecode<IJWTHeader>(token, { header: true })
        if (sub) {
          identify(sub)
          hasIdentified = true
        }
      } catch {
        // silently ignore decode errors
      }
    },
    [identify],
  )

  const loginOrRedirect = useCallback(async () => {
    const token = await getToken()
    const loggedByStoredToken = token ? await signInByToken(token) : false

    if (token && !loggedByStoredToken) {
      // Token guardado velho/expirado não pode derrubar a sessão do cookie:
      // descarta e deixa o upgrade abaixo emitir um token novo
      await removeToken()
    }

    // Rotas públicas de auth opcional (página de vendas): não dispara o
    // POST /auth/upgrade — visitante sem cookie geraria um 401 no console em
    // toda visita. Quem tem token guardado válido já entrou pelo
    // signInByToken acima; o resto navega como guest.
    if (isOptionalAuthRoute(window.location.pathname)) {
      setLoggedLoading(false)
      return
    }

    try {
      const { data } = await authUpgrade()
      await setToken(data.token)
      client.defaults.headers.Authorization = `Bearer ${data.token}`
      setLogged(true)
      setSessionToken(data.token)
      apolloClient.defaultContext.headers = {
        authorization: `Bearer ${data.token}`,
      }
      const { data: userData, error } = await getUser()
      if (error?.message === 'Token is invalid') {
        handleLogout()
        return
      }
      if (userData) {
        setUser(userData.user)
      }
      identifyOnce(data.token)

      setLoggedLoading(false)
    } catch (error) {
      // Rotas de auth opcional (página de vendas) funcionam deslogadas: sem
      // cookie o upgrade falha, mas o visitante fica na página como guest
      if (isOptionalAuthRoute(window.location.pathname)) {
        setLogged(false)
        setLoggedLoading(false)
        return
      }
      // O cookie do core é cookie de sessão do browser (morre ao fechar o
      // navegador) e o JWT guardado vale muito mais tempo. Quem já autenticou
      // pelo token guardado continua logado: derrubar aqui manda o aluno pro
      // login com sessão válida na mão e ele volta pra cá em looping.
      if (loggedByStoredToken && token) {
        identifyOnce(token)
        // O cookie do core é cookie de sessão do browser (morre ao fechar o
        // navegador) e o JWT guardado vale muito mais tempo. Recria a sessão
        // de cookie para que os downloads de mídia (PDF/ebook) continuem
        // autenticados. Antes de liberar a tela: o aluno que clica no ebook
        // assim que a aula abre corria a requisição do PDF contra o cookie.
        // Best-effort: um eventual erro não derruba o login.
        await restoreSession(token).catch(() => {})
        setLoggedLoading(false)
        return
      }
      handleLogout({ revokeSession: false })
      setLogged(false)
    }
    // É necessário não ter dependências para que o useEffect seja executado apenas uma vez e não entre em loop infinito
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (shouldSkipRoute) return
    if (logged || !loggedLoading) return
    loginOrRedirect()
  }, [logged, loggedLoading, loginOrRedirect, shouldSkipRoute])

  useEffect(() => {
    if (shouldSkipRoute) return
    if (isPublic) return
    if (isOptionalAuthRoute(pathname)) return
    if (!rootNavigationState.key) return
    if (!logged && !loggedLoading) {
      handleLogout()
    }
  }, [
    pathname,
    handleLogout,
    logged,
    loggedLoading,
    rootNavigationState.key,
    shouldSkipRoute,
  ])

  useEffect(() => {
    if (!shouldSkipRoute) return
    setLoggedLoading(false)
  }, [shouldSkipRoute])

  const contextValue = useMemo(
    () => ({ logged, loggedLoading, token, signInByToken, user }),
    [logged, loggedLoading, token, signInByToken, user],
  )

  if (loggedLoading) return <LoadingScreen />

  return (
    <SessionContext.Provider value={contextValue}>
      {children}
    </SessionContext.Provider>
  )
}
